import React, { createContext, useContext, useCallback, useRef } from 'react';

interface ISocketEvent {
  type: string;
  data: unknown;
  esNumber?: string;
  messageType?: string;
}

type SocketEventListener = (event: ISocketEvent) => void;

interface ISocketContext {
  emit: (event: ISocketEvent) => void;
  subscribe: (
    eventPattern: string,
    listener: SocketEventListener
  ) => () => void;
}

const SocketContext = createContext<ISocketContext | null>(null);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const listenersRef = useRef<Map<string, Set<SocketEventListener>>>(new Map());

  const emit = useCallback((event: ISocketEvent) => {
    const listeners = listenersRef.current;

    // Handle specific event patterns
    let eventKey = event.type;
    if (event.type === 'module' && event.esNumber && event.messageType) {
      eventKey = `module.${event.esNumber}.${event.messageType}`;
    }
    if (event.type === 'provisioning' && event.esNumber && event.messageType) {
      eventKey = `provisioning.${event.esNumber}.${event.messageType}`;
    }

    const eventListeners = listeners.get(eventKey);
    if (eventListeners) {
      eventListeners.forEach((listener) => listener(event));
    }

    // Also emit to generic event type listeners
    const genericListeners = listeners.get(event.type);
    if (genericListeners && eventKey !== event.type) {
      genericListeners.forEach((listener) => listener(event));
    }
  }, []);

  const subscribe = useCallback(
    (eventPattern: string, listener: SocketEventListener) => {
      const listeners = listenersRef.current;

      if (!listeners.has(eventPattern)) {
        listeners.set(eventPattern, new Set());
      }

      listeners.get(eventPattern)?.add(listener);

      // Return unsubscribe function
      return () => {
        const eventListeners = listeners.get(eventPattern);
        if (eventListeners) {
          eventListeners.delete(listener);
          if (eventListeners.size === 0) {
            listeners.delete(eventPattern);
          }
        }
      };
    },
    []
  );

  const contextValue: ISocketContext = {
    emit,
    subscribe,
  };

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};
